import { vec3 } from 'gl-matrix';
import { AudioBusId } from './AudioBus';
import { AudioClip } from './AudioClip';

export type VoiceOptions = {
  bus: AudioBusId;
  /** The bus GainNode the voice should feed into. */
  busInput: AudioNode;
  /** Linear gain in addition to clip.config.gain. */
  volume: number;
  /** playbackRate multiplier in addition to clip.pickPitch(). */
  pitch: number;
  /** null → non-spatial 2D, no panner inserted. */
  position: vec3 | null;
  loop: boolean;
  hrtfEnabled: boolean;
};

let nextVoiceId = 1;

/**
 * Single live audio playback. Wraps source → clipGain → [panner] → busInput.
 * Decoded clips use AudioBufferSourceNode with sprite offset/duration;
 * streaming clips wire HTMLAudioElement through createMediaElementSource.
 */
export class Voice {
  readonly id = nextVoiceId++;
  readonly clipId: string;
  readonly bus: AudioBusId;
  readonly startedAt: number;

  private readonly ctx: AudioContext;
  private readonly clipGain: GainNode;
  private readonly panner: PannerNode | null;
  private readonly source: AudioBufferSourceNode | MediaElementAudioSourceNode;
  private readonly streamingEl: HTMLAudioElement | null;
  private _ended = false;
  private endedCallbacks: Array<() => void> = [];

  constructor(ctx: AudioContext, clip: AudioClip, opts: VoiceOptions) {
    this.ctx = ctx;
    this.clipId = clip.config.id;
    this.bus = opts.bus;
    this.startedAt = ctx.currentTime;

    const baseGain = Math.max(0, clip.config.gain * opts.volume);
    this.clipGain = ctx.createGain();
    this.clipGain.gain.value = baseGain;

    if (opts.position && clip.config.spatial) {
      const panner = ctx.createPanner();
      const s = clip.config.spatial;
      panner.panningModel = opts.hrtfEnabled ? 'HRTF' : 'equalpower';
      panner.distanceModel = s.distanceModel;
      panner.refDistance = s.refDistance;
      panner.maxDistance = s.maxDistance;
      panner.rolloffFactor = s.rolloffFactor;
      setPannerPosition(panner, opts.position, ctx.currentTime);
      this.panner = panner;
    } else {
      this.panner = null;
    }

    const playbackRate = clip.pickPitch() * opts.pitch;

    if (clip.asset.kind === 'decoded') {
      const variant = clip.pickVariant();
      const file = clip.fileFor(variant);
      const buffer = clip.asset.buffers.get(file);
      if (!buffer) {
        throw new Error(`Audio clip "${clip.config.id}" variant "${variant.name}" missing buffer for ${file}`);
      }
      const offset = variant.offset ?? 0;
      const duration = variant.duration;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.playbackRate.value = playbackRate;
      src.loop = opts.loop;
      if (opts.loop) {
        src.loopStart = offset;
        src.loopEnd = duration !== undefined ? offset + duration : buffer.duration;
      }
      src.onended = () => this.handleEnded();
      this.source = src;
      this.streamingEl = null;
      this.connect(opts.busInput);
      if (opts.loop || duration === undefined) {
        // Loops ignore duration (keep looping until stop). Whole-file variants
        // also pass no duration so playback runs to the natural end.
        src.start(0, offset);
      } else {
        src.start(0, offset, duration);
      }
    } else {
      const variant = clip.pickVariant();
      const file = clip.fileFor(variant);
      const url = clip.asset.urls.get(file);
      if (!url) {
        throw new Error(`Audio clip "${clip.config.id}" variant "${variant.name}" missing URL for ${file}`);
      }
      const el = new Audio();
      el.src = url;
      el.loop = opts.loop;
      el.playbackRate = playbackRate;
      el.preload = 'auto';
      el.crossOrigin = 'anonymous';
      el.onended = () => this.handleEnded();
      const node = ctx.createMediaElementSource(el);
      this.source = node;
      this.streamingEl = el;
      this.connect(opts.busInput);
      // Best-effort autoplay. Note an asymmetry vs decoded clips: a
      // BufferSourceNode scheduled on a suspended AudioContext starts on
      // resume, but HTMLAudioElement.play() rejects under the same conditions
      // and the voice silently produces no audio. Decoded one-shots fired
      // before resumeOnUserGesture work; streaming one-shots don't.
      el.play().catch(() => {
        /* gesture not yet granted; voice is effectively dead */
      });
    }
  }

  private connect(busInput: AudioNode): void {
    if (this.panner) {
      this.source.connect(this.clipGain).connect(this.panner).connect(busInput);
    } else {
      this.source.connect(this.clipGain).connect(busInput);
    }
  }

  get ended(): boolean {
    return this._ended;
  }

  onEnded(cb: () => void): void {
    if (this._ended) cb();
    else this.endedCallbacks.push(cb);
  }

  setPosition(p: vec3): void {
    if (!this.panner) return;
    setPannerPosition(this.panner, p, this.ctx.currentTime);
  }

  setVolume(volume: number, fadeSeconds: number = 0): void {
    const target = Math.max(0, volume);
    const param = this.clipGain.gain;
    const now = this.ctx.currentTime;
    if (fadeSeconds <= 0) {
      param.cancelScheduledValues(now);
      param.setValueAtTime(target, now);
    } else {
      const current = param.value;
      param.cancelScheduledValues(now);
      param.setValueAtTime(current, now);
      param.linearRampToValueAtTime(target, now + fadeSeconds);
    }
  }

  stop(fadeSeconds: number = 0): void {
    if (this._ended) return;
    const now = this.ctx.currentTime;
    if (fadeSeconds > 0) {
      const param = this.clipGain.gain;
      param.cancelScheduledValues(now);
      param.setValueAtTime(param.value, now);
      param.linearRampToValueAtTime(0.0001, now + fadeSeconds);
      this.scheduleHardStop(fadeSeconds);
    } else {
      this.hardStop();
    }
  }

  private scheduleHardStop(seconds: number): void {
    setTimeout(() => this.hardStop(), Math.ceil(seconds * 1000));
  }

  private hardStop(): void {
    if (this._ended) return;
    try {
      if (this.streamingEl) {
        this.streamingEl.pause();
        this.streamingEl.src = '';
      } else {
        (this.source as AudioBufferSourceNode).stop();
      }
    } catch {
      /* already stopped */
    }
    this.handleEnded();
  }

  private handleEnded(): void {
    if (this._ended) return;
    this._ended = true;
    try {
      this.source.disconnect();
      this.clipGain.disconnect();
      this.panner?.disconnect();
    } catch {
      /* nodes may already be disconnected */
    }
    const cbs = this.endedCallbacks;
    this.endedCallbacks = [];
    for (const cb of cbs) cb();
  }
}

function setPannerPosition(panner: PannerNode, p: vec3, when: number): void {
  // Modern AudioParam path; fall back to deprecated setPosition for older Safari.
  if (panner.positionX) {
    panner.positionX.setValueAtTime(p[0], when);
    panner.positionY.setValueAtTime(p[1], when);
    panner.positionZ.setValueAtTime(p[2], when);
  } else {
    (panner as unknown as { setPosition: (x: number, y: number, z: number) => void }).setPosition(
      p[0],
      p[1],
      p[2],
    );
  }
}
